import React from "react"
import { useParams } from "react-router-dom"
import { Dialog, DialogContent } from "@material-ui/core"
import DialogTitle from "@material-ui/core/DialogTitle/DialogTitle"
import Button from "@material-ui/core/Button/Button"
import DialogActions from "@material-ui/core/DialogActions/DialogActions"
import { useKnowmixStep } from "../../../../../hooks/data/knowmix/useKnowmixStep"
import useCreator from "../../../../../hooks/useCreator"
import {
  createWork,
  finishWork,
  getKnowmix,
  updateWork,
} from "../../../../../library/store/actions/creators/knowmixCreators"
import Loader from "../../../../../components/ui/loader"
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator"
import TrueFalseQuestion from "./components/questions/trueFalseQuestion"
import MultipleChoiceQuestion from "./components/questions/multipleChoiceQuestion"
import TextAnswerQuestion from "./components/questions/textAnswerQeustion"
import FillBlankQuestion from "./components/questions/fillBlankQuestion"
import MatchingQuestion from "./components/questions/matchingQuestion"
import { urlValidation } from "../../../../../library/utils/validators"
import { FileLoader } from "../../../../../components/formComponents"
import AddOutlined from "@material-ui/icons/AddOutlined"
import { useSelector } from "react-redux"
import FormText from "../../../../../components/v2/layout/forms/formText"

const StepTakeModal = ({ open, onClose, stepId, knowmixId }) => {
  const params = useParams()
  const creator = useCreator()

  const { token } = useSelector(({ auth }) => auth.userData)

  const [work, setWork] = React.useState(null)
  const [isMounted, setIsMounted] = React.useState(false)
  const [link, setLink] = React.useState("")

  const [attachments, setAttachments] = React.useState([])
  const handleFileInputOpen = () => {
    document.getElementById("attachments").click()
  }

  const { step, isFetchedAfterMount } = useKnowmixStep(
    {
      knowmixId: knowmixId || parseInt(params.knowmixId, 10),
      stepId,
    },
    { enabled: !!knowmixId || !!params.knowmixId }
  )

  React.useEffect(() => {
    ValidatorForm.addValidationRule("isLink", (value) => urlValidation(value))
  }, [])

  React.useEffect(() => {
    if (step && !isMounted) {
      ;(async () => {
        if (step.type === 1) {
          await creator(createWork({ step_id: stepId }))
        }

        setWork(() => ({
          ...step,
          questions: step.questions.length
            ? step.questions.map((q) => {
                if ([1, 2].includes(q.type)) {
                  return {
                    ...q,
                    user_answer_options: q.user_answer_options || [],
                  }
                }
                if (q.type === 3) {
                  return {
                    ...q,
                    user_answer_text: q.user_answer_text || "",
                  }
                }
                if (q.type === 4) {
                  return {
                    ...q,
                    user_answer_text_options: q.user_answer_text_options || [
                      "",
                    ],
                  }
                }
                if (q.type === 5) {
                  return {
                    ...q,
                    user_answer_options:
                      q.user_answer_options ||
                      new Array(q.questions.length).fill(""),
                  }
                }

                return q
              })
            : [],
        }))
        setIsMounted(true)
      })()
    }
  }, [step])

  const onSaveChanges = async (currentWorkState) => {
    const data = currentWorkState || work

    const answerTextOptions = (q) => {
      switch (q.type) {
        case 1:
        case 2:
          return q?.user_answer_options?.filter((o) => o !== "")?.length
            ? q.user_answer_options
            : undefined

        case 5:
          return q?.user_answer_options?.filter((o) => o !== "")?.length ===
            q.questions.length
            ? q.user_answer_options
            : undefined

        default:
          return undefined
      }
    }
    await creator(
      updateWork({
        step_id: stepId,
        questions: data.questions.map((q) => ({
          id: q.id,
          answer_text: q.user_answer_text || undefined,
          answer_options: answerTextOptions(q),
          answer_text_options:
            q.type === 4 ? q.user_answer_text_options : undefined,
        })),
        link: "https://beta.knowstory",
        attachment_id: attachments.length ? attachments[0].id : undefined,
      })
    )
  }

  const onSubmit = async () => {
    if (!work.questions.length) {
      await onSaveChanges()
    }
    await creator(finishWork({ step_id: stepId }))
    await creator(getKnowmix({ id: parseInt(params.knowmixId, 10) }))
    onClose()
  }

  const handleQuestionStateChange = async (e, qIdx, oIdx, type) => {
    switch (type) {
      case 1: {
        const updatedData = {
          ...work,
          questions: work.questions.map((q, q_i) =>
            q_i === qIdx
              ? {
                  ...q,
                  user_answer_options: [oIdx],
                }
              : q
          ),
        }
        setWork(updatedData)
        return await onSaveChanges(updatedData)
      }

      case 2: {
        const updatedData = {
          ...work,
          questions: work.questions.map((q, q_i) =>
            q_i === qIdx
              ? {
                  ...q,
                  user_answer_options: e.target.checked
                    ? q.user_answer_options
                      ? [...q.user_answer_options, oIdx]
                      : [oIdx]
                    : q.user_answer_options.filter((a_o) => a_o !== oIdx),
                }
              : q
          ),
        }
        setWork(updatedData)
        return await onSaveChanges(updatedData)
      }

      case 3: {
        const updatedData = {
          ...work,
          questions: work.questions.map((q, q_i) =>
            q_i === qIdx
              ? {
                  ...q,
                  user_answer_text: e.target.value,
                }
              : q
          ),
        }
        return setWork(updatedData)
      }

      case 4: {
        const updatedData = {
          ...work,
          questions: work.questions.map((q, q_i) =>
            q_i === qIdx
              ? {
                  ...q,
                  user_answer_text_options: e.target.value
                    ? [e.target.value]
                    : undefined,
                }
              : q
          ),
        }
        return setWork(updatedData)
      }

      case 5: {
        const updatedData = {
          ...work,
          questions: work.questions.map((q, q_i) => {
            if (q_i === qIdx) {
              const updatedAnswers = q?.user_answer_options?.length
                ? q.user_answer_options
                : q.questions.map(() => "")

              return {
                ...q,
                user_answer_options: updatedAnswers.map((a_o, a_o_i) =>
                  a_o_i === oIdx ? e.target.value : a_o
                ),
              }
            }

            return q
          }),
        }
        setWork(updatedData)
        return await onSaveChanges(updatedData)
      }

      default:
        return
    }
  }

  return (
    <Dialog
      aria-labelledby="step-modal-title"
      onClose={onClose}
      open={open}
      maxWidth="md"
    >
      <ValidatorForm onSubmit={onSubmit} className="form">
        <DialogTitle id="step-modal-title" className="text-center">
          Complete work
        </DialogTitle>
        <DialogContent>
          <div className="form form--modal">
            {work && isFetchedAfterMount ? (
              <>
                <FormText className={"mb30 text-center"}>
                  <p className="color-black-54">Step</p>
                  <h3 className={"mb15"}>{step?.name}</h3>
                  <p className="color-black-54">Instructions</p>
                  <p>{step?.description}</p>
                </FormText>
                {!step.questions.length ? (
                  <FormText className={"mb30 text-center"}>
                    <div className="box">
                      <div className="box__content">
                        <div className="question-container text-secondary">
                          This step has no questions.
                        </div>
                        <div className="question-container text-secondary">
                          Complete step to notify an examiner about your
                          progress.
                        </div>
                      </div>
                    </div>
                  </FormText>
                ) : (
                  <>
                    <div className="box">
                      <div className="box__content">
                        <p className="box__subheading">Questions</p>
                        {work.questions.map((q, i) => {
                          return (
                            <div className="question-container" key={i}>
                              {q.type === 1 && (
                                <TrueFalseQuestion
                                  question={q}
                                  questionIdx={i}
                                  isCompleted={step.type === 3}
                                  onChange={(e, qIdx, oIdx) =>
                                    handleQuestionStateChange(e, qIdx, oIdx, 1)
                                  }
                                />
                              )}
                              {q.type === 2 && (
                                <MultipleChoiceQuestion
                                  question={q}
                                  questionIdx={i}
                                  isCompleted={step.type === 3}
                                  onChange={(e, qIdx, oIdx) =>
                                    handleQuestionStateChange(e, qIdx, oIdx, 2)
                                  }
                                />
                              )}
                              {q.type === 3 && (
                                <TextAnswerQuestion
                                  question={q}
                                  questionIdx={i}
                                  isCompleted={step.type === 3}
                                  onChange={(e, qIdx) =>
                                    handleQuestionStateChange(
                                      e,
                                      qIdx,
                                      undefined,
                                      3
                                    )
                                  }
                                  onBlur={() => onSaveChanges()}
                                />
                              )}
                              {q.type === 4 && (
                                <FillBlankQuestion
                                  question={q}
                                  questionIdx={i}
                                  isCompleted={step.type === 3}
                                  onChange={(e, qIdx) =>
                                    handleQuestionStateChange(
                                      e,
                                      qIdx,
                                      undefined,
                                      4
                                    )
                                  }
                                  onBlur={() => onSaveChanges()}
                                />
                              )}
                              {q.type === 5 && (
                                <MatchingQuestion
                                  question={q}
                                  questionIdx={i}
                                  isCompleted={step.type === 3}
                                  onChange={(e, qIdx, oIdx) =>
                                    handleQuestionStateChange(e, qIdx, oIdx, 5)
                                  }
                                />
                              )}
                              {/*{!isLast(work.questions, i) && (*/}
                              <div className="form__divider" />
                              {/*)}*/}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </>
                )}
                <div className="question-container text-secondary mb30 mt15">
                  You can add an attachment and/or a link to show what you've
                  done.
                </div>
                <div className="form__fieldset">
                  <label htmlFor="link" className="form__label">
                    Link
                  </label>
                  <div className="form__fieldbox">
                    <div className="form__input">
                      <TextValidator
                        id="link"
                        name="link"
                        value={link}
                        placeholder="Type conference link"
                        className="edit-form__input"
                        margin="normal"
                        fullWidth
                        validators={["isLink", "maxStringLength:250"]}
                        errorMessages={[
                          "Invalid URL",
                          "Max length is 250 characters",
                        ]}
                        onChange={(e) => setLink(e.target.value)}
                        disabled={step.type === 3}
                      />
                    </div>
                  </div>
                </div>
                <div className="form__fieldset">
                  <span className="form__label">Attachment</span>
                  <div className="form__fieldbox">
                    <div className="form__attachments">
                      <FileLoader
                        attachmentId={"attachments"}
                        blobs={attachments}
                        onUpload={({ blobs }) => setAttachments(blobs)}
                        token={token}
                        attachmentOnly={true}
                        limit={1}
                      />
                      {!attachments.length && (
                        <button
                          className="form__add-input"
                          type="button"
                          onClick={handleFileInputOpen}
                          disabled={step.type === 3}
                        >
                          <AddOutlined /> Add attachment
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <Loader />
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose}>
            Close
          </Button>
          {step?.type === 2 && (
            <Button color="primary" type={"submit"}>
              Submit
            </Button>
          )}
        </DialogActions>
      </ValidatorForm>
    </Dialog>
  )
}

export default StepTakeModal
